import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconX } from '@tabler/icons-react';
import { getProfile } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { copyText } from '../utils/copyText';
import { formatReferralCode } from '../utils/referral';
import Loader from './ui/Loader';
import ErrorMessage from './ui/ErrorMessage';
import PageHeader from './ui/PageHeader';
import ChangePasswordModal from './ChangePasswordModal';
import AccountProfileHero from './account/AccountProfileHero';
import AccountReferralCards from './account/AccountReferralCards';
import AccountSettingsList from './account/AccountSettingsList';
import { track } from '../utils/analytics';
import './AccountPage.scss';

const SUCCESS_BANNER_MS = 5000;

export default function AccountPage() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileError, setProfileError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const { user, logout } = useAuth();
    const { theme, setTheme, isUserSet } = useTheme();
    const navigate = useNavigate();

    const loadProfile = useCallback(async () => {
        setLoading(true);
        setProfileError('');
        try {
            const data = await getProfile();
            setProfile(data);
        } catch (err) {
            setProfile(null);
            setProfileError(err.message || 'Не удалось загрузить профиль');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    useEffect(() => {
        if (!successMessage) return undefined;
        const timer = setTimeout(() => setSuccessMessage(''), SUCCESS_BANNER_MS);
        return () => clearTimeout(timer);
    }, [successMessage]);

    const handleCopyCode = async () => {
        if (!profile?.referral_code) return;
        await copyText(profile.referral_code);
        track('referral_code_copied');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleBindSuccess = (referredBy) => {
        setProfile((prev) => ({ ...prev, referred_by: referredBy }));
        setSuccessMessage(`Код ${formatReferralCode(referredBy.code)} привязан · 10% кэшбэк с покупок`);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    if (loading) {
        return <Loader text="Загружаем профиль..." />;
    }

    const referralsCount = profile?.referral_stats?.referrals_count ?? 0;
    const totalEarned = profile?.referral_stats?.total_earned ?? 0;

    return (
        <div className="account-page">
            <PageHeader title="Аккаунт" />

            <ErrorMessage message={profileError} onRetry={loadProfile} />

            {successMessage && (
                <div className="banner-success account-page__banner" role="status">
                    <span>{successMessage}</span>
                    <button
                        type="button"
                        className="account-page__banner-dismiss"
                        onClick={() => setSuccessMessage('')}
                        aria-label="Закрыть"
                    >
                        <IconX size={16} stroke={1.5} />
                    </button>
                </div>
            )}

            {!profileError && profile && (
                <div className="account-page__stack">
                    <AccountProfileHero
                        login={user?.login}
                        points={profile.referral_points}
                    />

                    <AccountReferralCards
                        referralCode={profile.referral_code}
                        referredBy={profile.referred_by}
                        referralsCount={referralsCount}
                        totalEarned={totalEarned}
                        copied={copied}
                        onCopyCode={handleCopyCode}
                        onBindSuccess={handleBindSuccess}
                    />

                    <AccountSettingsList
                        theme={theme}
                        setTheme={setTheme}
                        isUserSet={isUserSet}
                        onChangePassword={() => setShowPasswordModal(true)}
                        onLogout={handleLogout}
                    />
                </div>
            )}

            {showPasswordModal && (
                <ChangePasswordModal
                    login={user?.login}
                    onClose={() => setShowPasswordModal(false)}
                    onSuccess={setSuccessMessage}
                />
            )}
        </div>
    );
}
